import { ChangeDetectionStrategy,Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';

import { Library } from 'app/shared/graph';

import { LibrarySettingsService } from './library-settings.service';

type LibraryBrancheDefinition = { owner: string, branch: string };

@Component({
    selector: 'admin-library-branches-selector',
    imports: [
        MatIconModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        ReactiveFormsModule,
        FormsModule,
        MatSelectModule,
        MatAutocompleteModule
    ],
    templateUrl: './library-branches-selector.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './library-branches-selector.component.scss'
})
export class LibraryBranchesSelectorComponent implements OnInit, OnChanges {
    private readonly libraryBranchServiceService: LibrarySettingsService = inject(LibrarySettingsService);

    @Input() libraryBranches: string | undefined | null = '';
    @Output() libraryBranchesChange = new EventEmitter<string | undefined | null>();
    libraries: Library[] = [];
    libraryByOwners: Map<string, Library> = new Map();
    libraryBranchDefinitions: LibraryBrancheDefinition[] = [];

    ngOnInit() {
        this.libraryBranchDefinitions = this.parseLibraryBranches(this.libraryBranches);
        this.libraryBranches = this.serializeLibraryBranches();
        this.libraryBranchServiceService.getLibrariesWithBranches().subscribe(libraries => {
            this.libraries = libraries;
            this.libraryByOwners = libraries.reduce((map, library) => {
                map.set(library.owner, library);
                return map;
            }, new Map<string, Library>())
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if ('libraryBranches' in changes) {
            if (changes.libraryBranches.previousValue != this.libraryBranches) {
                this.libraryBranchDefinitions = this.parseLibraryBranches(this.libraryBranches);
                this.libraryBranches = this.serializeLibraryBranches();
            }
        }
    }

    addBranchDefinition() {
        this.libraryBranchDefinitions.push({ owner: '', branch: ''  });
    }

    removeBranchDefinition(i: number) {
        this.libraryBranchDefinitions.splice(i, 1);
        this.updateLibraryBranches()
    }

    updateLibraryBranches() {
        this.libraryBranches = this.serializeLibraryBranches();
        this.libraryBranchesChange.emit(this.libraryBranches);
    }

    canAddBranches() {
        if (this.libraryBranchDefinitions.length === 0) {
            return true;
        }

        let lastLibraryBranchDefinitions = this.libraryBranchDefinitions[this.libraryBranchDefinitions.length - 1];
        if (!lastLibraryBranchDefinitions.owner) {
            return false;
        }
        if (!lastLibraryBranchDefinitions.branch) {
            return false;
        }

        return true;
    }

    private parseLibraryBranches(libraryBranches: string | undefined | null): LibraryBrancheDefinition[] {
        if (libraryBranches == null) {
            return [];
        }
        let libraryBranchDefinitions = [];
        let libraryBranchesSplit = libraryBranches?.split(';');
        for (let ownerWithBranch of libraryBranchesSplit) {
            let ownerWithBranchSplit = ownerWithBranch.split(':');
            libraryBranchDefinitions.push({
                owner: ownerWithBranchSplit[0],
                branch: ownerWithBranchSplit[1]
            } as LibraryBrancheDefinition);
        }
        return libraryBranchDefinitions;
    }

    private serializeLibraryBranches() {
        let libraryBranches = '';
        for (let libraryBranchDefinition of this.libraryBranchDefinitions) {
            if (!libraryBranchDefinition.owner) {
                continue;
            }
            if (!libraryBranchDefinition.branch) {
                continue;
            }
            if (libraryBranches.length > 0) {
                libraryBranches += ';';
            }
            libraryBranches += `${libraryBranchDefinition.owner}:${libraryBranchDefinition.branch}`;
        }
        return libraryBranches;
    }

    filterLibraries(owner: string) {
        return this.libraries.filter(library => library.owner.toLowerCase().includes(owner.toLowerCase()));
    }

    updateOwner(owner: string, brancheDefinition: LibraryBrancheDefinition) {
        brancheDefinition.owner = owner;
        if (!brancheDefinition.branch) {
            let library = this.libraryByOwners.get(brancheDefinition.owner);
            if (library && library.branches.length > 0) {
                brancheDefinition.branch = library.branches[library.branches.length - 1].name;
            }
        }
        this.updateLibraryBranches();
    }
}
